import * as AsyncLock from 'async-lock';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CredentialsModel } from '../models/credentials-model';
import { ModelFactory } from '../models/model-factory';
import { SessionModel } from '../models/session-model';
import { errors, storage as storageEnums } from '../utils/catalog';
import { ClientService } from './client.service';
import { StorageService } from './storage.service';
import { ErrorModel } from '../models/error-model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private lock: AsyncLock;

  constructor(private client: ClientService, private storage: StorageService) {
    this.lock = new AsyncLock();
  }

  public async auth(credentials: CredentialsModel): Promise<SessionModel> {
    const session = ModelFactory.newInstance(SessionModel, {
      ...(await this.client.post('auth/login', credentials)),
      cloudId: credentials.cloudId,
      username: credentials.username,
    });
    if (session.apiVersion < environment.api.minVersion) throw new ErrorModel(errors.unsupportedApiVersion);
    return session;
  }

  public async login(credentials: CredentialsModel): Promise<SessionModel> {
    const session = await this.auth(credentials);
    this.storage.put(storageEnums.keys.auth, session);
    return session;
  }

  public async refresh(): Promise<boolean> {
    const { cloudId, sessionId, refreshToken, ...otherProps } = this.storage.get(storageEnums.keys.auth, {});
    if (!refreshToken) return false;

    return this.lock.acquire(cloudId, async () => {
      const { sessionId: newSessionId } = this.storage.get(storageEnums.keys.auth, {});
      if (sessionId !== newSessionId) return true;

      try {
        const session = await this.client.post('auth/refresh', {
          refreshToken,
        });
        const auth = { ...otherProps, ...session, cloudId };
        this.storage.put(storageEnums.keys.auth, auth);
        return true;
      } catch (err) {
        return false;
      }
    });
  }

  public async changePassword(credentials: CredentialsModel): Promise<SessionModel> {
    const session = ModelFactory.newInstance(SessionModel, {
      ...(await this.client.post('auth/password', credentials)),
      cloudId: credentials.cloudId,
      username: credentials.username,
    });
    return session;
  }

  public async logout(): Promise<void> {
    this.storage.remove(storageEnums.keys.auth);
  }

  public async isLoggedIn(): Promise<boolean> {
    return !!this.storage.get(storageEnums.keys.auth);
  }
}
